import { ColorEnum } from '@/constants/color'
import { DeliveryTypeCategory } from '@/features/orders/constants/app'
import CustomerProfile from '@/features/pos/components/CustomerProfile'
import { usePortal } from '@/features/pos/components/CustomerProfile/contexts/Portal'
import {
    useFormCustomerField,
    useFormDeliveryTypeCategory,
    useFormSetCustomerField,
    useFormTriggerCustomerField,
    useFormTriggerDeliveryTimeField,
} from '@/features/pos/components/OrderFinalization/form'
import { CustomerInput } from '@/features/pos/components/OrderFinalization/form/types'
import ErrorMessage from '@/features/pos/components/OrderFinalization/parts/FormFields/parts/PhoneModeFields/parts/CustomerField/parts/ErrorMessage'
import { fieldStyles } from '@/features/pos/components/OrderFinalization/parts/FormFields/styles'
import FieldButton from '@/features/ui/buttons/components/FieldButton'
import PersonIcon from '@/features/ui/icons/components/PersonIcon'
import { useMeasuredStyles } from '@/features/ui/inputs/components/ValidationStatusMessage/hooks'
import { useScreenSize } from '@/hooks/screen'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'

interface CustomerFieldProps {
    deliveryTypeCategories: DeliveryTypeCategory[]
}

const CustomerField: FC<CustomerFieldProps> = ({ deliveryTypeCategories }) => {
    const field = useFormCustomerField()
    const [deliveryTypeCategory, setDeliveryTypeCategory] = useFormDeliveryTypeCategory()
    const setCustomerField = useFormSetCustomerField()
    const triggerCustomerField = useFormTriggerCustomerField()
    const triggerDeliveryTimeField = useFormTriggerDeliveryTimeField()

    const [isModalOpen, setIsModalOpen] = useState(false)
    const openModalFrameRef = useRef<number | null>(null)

    const wrapperRef = useRef<View>(null)

    const [measuredErrorMessageStyles, measureErrorMessageStyles] = useMeasuredStyles(wrapperRef)

    const screenSize = useScreenSize()

    const { t } = useTranslation()

    const openModal = useCallback(() => {
        if (openModalFrameRef.current != null) {
            cancelAnimationFrame(openModalFrameRef.current)
        }

        openModalFrameRef.current = requestAnimationFrame(() => {
            setIsModalOpen(true)
            openModalFrameRef.current = null
        })
    }, [])

    const closeModal = useCallback(() => setIsModalOpen(false), [])

    useEffect(
        () => () => {
            if (openModalFrameRef.current != null) {
                cancelAnimationFrame(openModalFrameRef.current)
                openModalFrameRef.current = null
            }
        },
        []
    )

    const handleSave = useCallback(
        ({
            address,
            deliveryTypeCategory,
            name,
            phone,
        }: {
            address?: NonNullable<CustomerInput>['address']
            deliveryTypeCategory: DeliveryTypeCategory
            name: string
            phone: string
        }) => {
            const customer: CustomerInput = {
                name,
                phone,
                ...(address && { address }),
            }

            setCustomerField(customer)
            setDeliveryTypeCategory(deliveryTypeCategory)
            triggerCustomerField()
            setTimeout(() => triggerDeliveryTimeField())
            closeModal()
        },
        [
            closeModal,
            setCustomerField,
            setDeliveryTypeCategory,
            triggerCustomerField,
            triggerDeliveryTimeField,
        ]
    )

    const memoizedCustomerProfile = useMemo(
        () => (
            <CustomerProfile
                deliveryTypeCategories={deliveryTypeCategories}
                initialCustomer={field}
                deliveryTypeCategory={deliveryTypeCategory}
                onCancel={closeModal}
                onSave={handleSave}
            />
        ),
        [closeModal, deliveryTypeCategories, deliveryTypeCategory, field, handleSave]
    )

    usePortal(isModalOpen, memoizedCustomerProfile)

    const customerPrimaryLine = [field?.phone, field?.name].filter(Boolean).join(' · ')
    const customerSecondaryLine = field?.address?.address ?? ''
    const hasCustomerAddress = !!customerSecondaryLine
    const customerValue = [customerPrimaryLine, customerSecondaryLine].filter(Boolean).join('\n')
    const hasCustomerValue = !!customerValue

    return (
        <View onLayout={measureErrorMessageStyles} ref={wrapperRef}>
            <FieldButton
                label={t('pos.orderFinalization.inputs.customer.label')}
                placeholder={t('pos.orderFinalization.inputs.customer.placeholder')}
                value={customerValue}
                valueNumberOfLines={hasCustomerAddress ? 2 : 1}
                hideLabelWhenValue={hasCustomerAddress}
                onPress={openModal}
                FrontIcon={<PersonIcon fill={ColorEnum.BRICK_600} />}
                style={screenSize === 'small' ? fieldStyles.smallScreenField : null}
                valueTextStyle={
                    hasCustomerValue && hasCustomerAddress ? styles.filledValueText : undefined
                }
            />
            <ErrorMessage measuredStyles={measuredErrorMessageStyles} />
        </View>
    )
}

const styles = StyleSheet.create({
    filledValueText: {
        fontSize: 10,
        lineHeight: 18,
    },
})

export default CustomerField
